import React from 'react'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'

import {
  UI_COLOR_BORDER_UTILITY,
  UI_COLOR_BORDER_UTILITY_DISABLED,
  UI_COLOR_BORDER_UTILITY_HOVER,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_ALT_HOVER,
  UI_COLOR_SHADOW_BUTTON,
  UI_COLOR_SHADOW_SOFT,
  UI_COLOR_SURFACE_DISABLED,
  UI_COLOR_SURFACE_UTILITY_HOVER,
  UI_COLOR_TEXT_UTILITY,
  UI_COLOR_TEXT_UTILITY_DISABLED,
  UI_COLOR_TEXT_UTILITY_HOVER,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import GoogleIcon from '../icon/GoogleIcon'
import ActionButton from './ActionButton'

const DEFAULT_UTILITY_BUTTON_SX = {
  width: 38,
  height: 38,
  borderRadius: 1,
  border: `1px solid ${UI_COLOR_BORDER_UTILITY}`,
  color: UI_COLOR_TEXT_UTILITY,
  backgroundColor: UI_COLOR_WHITE,
  boxShadow: 'none',
  transition:
    'background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
  '&:hover': {
    borderColor: UI_COLOR_BORDER_UTILITY_HOVER,
    color: UI_COLOR_TEXT_UTILITY_HOVER,
    backgroundColor: UI_COLOR_SURFACE_UTILITY_HOVER,
    boxShadow: UI_COLOR_SHADOW_SOFT
  },
  '&.Mui-disabled': {
    borderColor: UI_COLOR_BORDER_UTILITY_DISABLED,
    color: UI_COLOR_TEXT_UTILITY_DISABLED,
    backgroundColor: UI_COLOR_SURFACE_DISABLED,
    boxShadow: 'none'
  }
}

/*
 * Ejemplo de uso:
 * <TableActionButtons
 *   utilityActions={[
 *     { label: 'Copiar tabla', icon: 'content_copy', onClick: onCopy },
 *     { label: 'Descargar tabla', icon: 'download', onClick: onDownload }
 *   ]}
 *   primaryAction={{ label: 'Escanear', icon: 'barcode_scanner', onClick: onScan }}
 * />
 */
export default function TableActionButtons({
  utilityActions = [],
  primaryAction,
  gap = 1,
  fullWidthOnMobile = false,
  sx
}) {
  const visibleUtilityActions = utilityActions.filter((action) => action.visible !== false)
  const showPrimaryAction = primaryAction && primaryAction.visible !== false
  const { sx: primaryButtonPropsSx, ...primaryButtonProps } = primaryAction?.buttonProps || {}

  return (
    <Stack
      direction="row"
      spacing={gap}
      useFlexGap
      sx={{
        width: { xs: fullWidthOnMobile ? '100%' : 'auto', md: 'auto' },
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        ...sx
      }}
    >
      {visibleUtilityActions.map((action, index) => {
        const {
          key,
          label,
          tooltip = label,
          icon,
          iconSize = 20,
          iconFilled = false,
          iconNode,
          ariaLabel = label,
          onClick,
          disabled = false,
          sx: actionSx,
          buttonProps = {}
        } = action
        const { sx: buttonPropsSx, ...muiButtonProps } = buttonProps
        const utilityButton = (
          <IconButton
            {...muiButtonProps}
            aria-label={ariaLabel}
            onClick={onClick}
            disabled={disabled}
            sx={{ ...DEFAULT_UTILITY_BUTTON_SX, ...actionSx, ...buttonPropsSx }}
          >
            {iconNode || (
              <GoogleIcon name={icon} size={iconSize} filled={iconFilled} color="inherit" />
            )}
          </IconButton>
        )

        return (
          <Tooltip key={key || `${label}-${index}`} title={tooltip || ''}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              {utilityButton}
            </Box>
          </Tooltip>
        )
      })}

      {showPrimaryAction && (
        <ActionButton
          {...primaryButtonProps}
          text={primaryAction.text || primaryAction.label}
          icon={primaryAction.icon}
          iconSize={primaryAction.iconSize || 18}
          filled={primaryAction.iconFilled}
          color={primaryAction.color || UI_COLOR_PRIMARY}
          hoverColor={primaryAction.hoverColor || UI_COLOR_PRIMARY_ALT_HOVER}
          textColor={primaryAction.textColor}
          type={primaryAction.type}
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          fullWidth={primaryAction.fullWidth}
          sx={{
            minHeight: 38,
            px: 2.25,
            fontSize: 13,
            fontWeight: 500,
            borderRadius: 1,
            justifyContent: 'center',
            boxShadow: UI_COLOR_SHADOW_BUTTON,
            '& .MuiButton-startIcon': {
              ml: 0,
              mr: 1
            },
            '& .material-symbols-outlined': {
              color: 'inherit'
            },
            '&:hover': {
              boxShadow: UI_COLOR_SHADOW_BUTTON
            },
            ...primaryAction.sx,
            ...primaryButtonPropsSx
          }}
        />
      )}
    </Stack>
  )
}
